/**
 * @file ProductVariants.tsx
 *
 * Manages a product's variant options (attributes that vary, like Size or
 * Colour). Lists existing options with their values as chips, and drives the
 * add/edit flow through {@link VariantOptionForm}. Deletion is confirmed with
 * the shared {@link ConfirmModal}. Controlled: the option list is owned by the
 * form and reported via `onChange`.
 */

import { useState } from 'react';
import { Action, Chip, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { ShopOptionValue, ShopProductAttribute } from '../../../api/shop-types';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { VariantOptionForm } from './VariantOptionForm';
import { countCombinations, slugify } from '../product-form-utils';
import {
  MAX_VARIANT_COMBINATIONS,
  MAX_VARIANT_OPTIONS,
} from '../product-form-constants';
import styles from '../ProductForm.module.css';

interface ProductVariantsProps {
  attributes: ShopProductAttribute[];
  /**
   * Reports the edited axes. `meta.confirmed` marks a change whose gesture already
   * carried its own warning (the whole-option delete modal), so the owner must not
   * raise its value-loss confirm a second time.
   */
  onChange: (attributes: ShopProductAttribute[], meta?: { confirmed?: boolean }) => void;
}

type DraftMode = 'idle' | 'add' | { editingSlug: string };

/**
 * Renders the variant-options manager.
 *
 * @param props - The current attributes and a change handler.
 *
 * @returns The variants section.
 */
export function ProductVariants({ attributes, onChange }: ProductVariantsProps) {
  const intl = useIntl();
  const [draft, setDraft] = useState<DraftMode>('idle');
  const [pendingDelete, setPendingDelete] = useState<ShopProductAttribute | null>(
    null,
  );
  // A refused option save (the edit would push the matrix past the combination cap),
  // shown beside the still-open editor so the values can be trimmed and re-saved.
  const [capError, setCapError] = useState<string | null>(null);

  const editingSlug = typeof draft === 'object' ? draft.editingSlug : null;
  // The client mirrors the commerce-engine ceiling on the number of variation axes, so a
  // 7th option is refused here rather than at the save round-trip.
  const atOptionCap = attributes.length >= MAX_VARIANT_OPTIONS;
  // The running total against the combination ceiling, shown with the options (the WSB
  // placement) because this is where the number is made.
  const combinationCount = countCombinations(attributes);

  const otherNames = (excludeSlug: string | null): string[] =>
    attributes
      .filter((a) => a.slug !== excludeSlug)
      .map((a) => a.name.toLowerCase());

  /**
   * The combination cap is on the PRODUCT of every axis, so it can only be judged with
   * all of them in hand — an option well inside its own value limit can still push the
   * matrix past the ceiling. Judged at entry (the WSB behaviour), keeping the editor
   * open, rather than surfacing only when the whole form is saved.
   */
  const exceedsCombinationCap = (candidate: ShopProductAttribute[]): boolean => {
    const total = countCombinations(candidate);
    if (total > MAX_VARIANT_COMBINATIONS) {
      setCapError(
        intl.formatMessage(adminMessages.formVariantTooMany, {
          count: total,
          max: MAX_VARIANT_COMBINATIONS,
        }),
      );
      return true;
    }
    setCapError(null);
    return false;
  };

  const saveNew = (name: string, values: ShopOptionValue[]): void => {
    const slug = slugify(name);
    const next: ShopProductAttribute = {
      id: slug,
      name,
      slug,
      position: attributes.length,
      visible: true,
      variation: true,
      options: values,
    };
    if (exceedsCombinationCap([...attributes, next])) {
      return;
    }
    onChange([...attributes, next]);
    setDraft('idle');
  };

  const saveEdit = (originalSlug: string, name: string, values: ShopOptionValue[]): void => {
    const slug = slugify(name);
    const next = attributes.map((a) =>
      a.slug === originalSlug ? { ...a, name, slug, id: slug, options: values } : a,
    );
    if (exceedsCombinationCap(next)) {
      return;
    }
    onChange(next);
    setDraft('idle');
  };

  const confirmDelete = (): void => {
    if (pendingDelete) {
      // This modal IS the warning for the delete gesture — mark the change confirmed so
      // the owner's value-loss guard doesn't ask the same question again.
      onChange(
        attributes.filter((a) => a.slug !== pendingDelete.slug),
        { confirmed: true },
      );
    }
    setPendingDelete(null);
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">
      <p className="gv-text-sm">
        <FormattedMessage {...adminMessages.formVariantsDescription} />
      </p>

      {attributes.map((attribute) =>
        editingSlug === attribute.slug ? (
          <VariantOptionForm
            key={attribute.slug}
            mode="edit"
            initialName={attribute.name}
            initialValues={attribute.options}
            existingNames={otherNames(attribute.slug)}
            onSave={(name, values) => saveEdit(attribute.slug, name, values)}
            onCancel={() => {
              setCapError(null);
              setDraft('idle');
            }}
          />
        ) : (
          <div
            key={attribute.slug}
            className={styles.variantRow}
            data-testid="product-form-variant-row"
            data-slug={attribute.slug}
          >
            <div className="gv-flex gv-flex-col gv-gap-xs">
              <span className={`gv-text-md ${styles.fieldLabel}`}>{attribute.name}</span>
              <div className={styles.chips}>
                {attribute.options.map((option) => (
                  <Chip key={option.value}>{option.value}</Chip>
                ))}
              </div>
            </div>
            <div className="gv-flex gv-gap-xs">
              <button
                type="button"
                className="gv-button gv-button-bare"
                data-testid="product-form-variant-edit"
                aria-label={intl.formatMessage(adminMessages.formEditOption)}
                onClick={() => setDraft({ editingSlug: attribute.slug })}
              >
                <Icon name="edit" size="small" />
              </button>
              <button
                type="button"
                className="gv-button gv-button-bare"
                data-testid="product-form-variant-delete"
                aria-label={intl.formatMessage(adminMessages.formDeleteVariantTitle)}
                onClick={() => setPendingDelete(attribute)}
              >
                <Icon name="delete" size="small" />
              </button>
            </div>
          </div>
        ),
      )}

      {combinationCount > 0 && (
        <span
          className="gv-text-sm gv-text-muted"
          data-testid="product-form-combination-count"
        >
          <FormattedMessage
            {...adminMessages.formVariantCombinationCount}
            values={{ count: combinationCount, max: MAX_VARIANT_COMBINATIONS }}
          />
        </span>
      )}

      {draft === 'add' ? (
        <VariantOptionForm
          mode="add"
          existingNames={otherNames(null)}
          onSave={saveNew}
          onCancel={() => {
            setCapError(null);
            setDraft('idle');
          }}
        />
      ) : atOptionCap ? (
        <p
          className="gv-text-sm gv-state-critical"
          role="alert"
          data-testid="product-form-option-cap"
        >
          <FormattedMessage
            {...adminMessages.formVariantMaxOptions}
            values={{ max: MAX_VARIANT_OPTIONS }}
          />
        </p>
      ) : (
        <div>
          <Action
            component="button"
            type="button"
            className="gv-flex gv-items-center gv-gap-xs"
            style={{ color: 'var(--color-state-form-active, #0066aa)' }}
            data-testid="product-form-add-variant"
            onClick={() => setDraft('add')}
          >
            <Icon name="add" size="small" />
            <span className="gv-text-sm gv-text-bold">
              <FormattedMessage {...adminMessages.formAddOption} />
            </span>
          </Action>
        </div>
      )}

      {capError && (
        <p
          className="gv-text-sm gv-state-critical"
          role="alert"
          data-testid="product-form-option-cap-error"
        >
          {capError}
        </p>
      )}

      {pendingDelete && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.formDeleteVariantTitle)}
          message={intl.formatMessage(adminMessages.formDeleteVariantMessage, {
            name: pendingDelete.name,
          })}
          confirmLabel={intl.formatMessage(adminMessages.productsDelete)}
          cancelLabel={intl.formatMessage(adminMessages.formCancel)}
          loadingLabel={intl.formatMessage(adminMessages.productsDelete)}
          confirmVariant="destructive"
          onPrimaryClick={confirmDelete}
          onSecondaryClick={() => setPendingDelete(null)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
